import cartService from '../../services/cartService';
import orderService from '../../services/orderService';
import paymentService from '../../services/paymentService';

// Action Types
export const CHECKOUT_ACTION_TYPES = {
  // Step management
  SET_CHECKOUT_STEP: 'SET_CHECKOUT_STEP',
  RESET_CHECKOUT: 'RESET_CHECKOUT',

  // Shipping address
  SET_SHIPPING_ADDRESS: 'SET_SHIPPING_ADDRESS',
  SET_BILLING_ADDRESS: 'SET_BILLING_ADDRESS',
  SET_SAME_AS_BILLING: 'SET_SAME_AS_BILLING',

  // Guest info
  SET_GUEST_INFO: 'SET_GUEST_INFO',

  // Payment
  SET_PAYMENT_METHOD: 'SET_PAYMENT_METHOD',

  // Order
  CREATE_ORDER_REQUEST: 'CREATE_ORDER_REQUEST',
  CREATE_ORDER_SUCCESS: 'CREATE_ORDER_SUCCESS',
  CREATE_ORDER_FAILURE: 'CREATE_ORDER_FAILURE',

  // Notes
  SET_ORDER_NOTES: 'SET_ORDER_NOTES',

  // Errors
  SET_CHECKOUT_ERROR: 'SET_CHECKOUT_ERROR',
  CLEAR_CHECKOUT_ERROR: 'CLEAR_CHECKOUT_ERROR'
};

// ============================================
// Step Management Actions
// ============================================

export const setCheckoutStep = (step) => ({
  type: CHECKOUT_ACTION_TYPES.SET_CHECKOUT_STEP,
  payload: step
});

export const resetCheckout = () => ({
  type: CHECKOUT_ACTION_TYPES.RESET_CHECKOUT
});

// ============================================
// Address Actions
// ============================================

export const setShippingAddress = (address) => ({
  type: CHECKOUT_ACTION_TYPES.SET_SHIPPING_ADDRESS,
  payload: address
});

export const setBillingAddress = (address) => ({
  type: CHECKOUT_ACTION_TYPES.SET_BILLING_ADDRESS,
  payload: address
});

export const setSameAsBilling = (same) => ({
  type: CHECKOUT_ACTION_TYPES.SET_SAME_AS_BILLING,
  payload: same
});

// ============================================
// Guest Info Actions
// ============================================

export const setGuestInfo = (info) => ({
  type: CHECKOUT_ACTION_TYPES.SET_GUEST_INFO,
  payload: info
});

// ============================================
// Payment Actions
// ============================================

export const setPaymentMethod = (method) => ({
  type: CHECKOUT_ACTION_TYPES.SET_PAYMENT_METHOD,
  payload: method
});

// ============================================
// Order Actions
// ============================================

export const createOrder = (formData, isAuthenticated) => async (dispatch) => {
  dispatch({ type: CHECKOUT_ACTION_TYPES.CREATE_ORDER_REQUEST });

  try {
    const validation = await cartService.validateCheckout();
    if (!validation.success || !validation.valid) {
      const details = validation.details || validation.data?.errors || [];
      const errorMessage = validation.error || validation.data?.error || 'Cart validation failed';
      const formattedError = details.length ? `${errorMessage}: ${details.join(', ')}` : errorMessage;

      dispatch({
        type: CHECKOUT_ACTION_TYPES.CREATE_ORDER_FAILURE,
        payload: formattedError
      });

      return {
        success: false,
        error: formattedError,
        details
      };
    }

    // Build order data from form
    const orderData = orderService.buildOrderData(formData, isAuthenticated);

    const result = await orderService.createOrder(orderData);

    if (result.success) {
      dispatch({
        type: CHECKOUT_ACTION_TYPES.CREATE_ORDER_SUCCESS,
        payload: result.order
      });

      // If Razorpay payment, initiate payment flow
      if (formData.paymentMethod === 'RAZORPAY' && result.order) {
        // Store order number for payment callback
        paymentService.storePaymentSession(
          result.order.order_number,
          null // Will be set after creating payment order
        );
      }

      return {
        success: true,
        order: result.order,
        paymentMethod: formData.paymentMethod
      };
    } else {
      dispatch({
        type: CHECKOUT_ACTION_TYPES.CREATE_ORDER_FAILURE,
        payload: result.error
      });
      return { success: false, error: result.error };
    }
  } catch (error) {
    const errorMessage = error.message || 'Failed to create order';
    dispatch({
      type: CHECKOUT_ACTION_TYPES.CREATE_ORDER_FAILURE,
      payload: errorMessage
    });
    return { success: false, error: errorMessage };
  }
};

// ============================================
// Notes Actions
// ============================================

export const setOrderNotes = (notes) => ({
  type: CHECKOUT_ACTION_TYPES.SET_ORDER_NOTES,
  payload: notes
});

// ============================================
// Error Actions
// ============================================

export const setCheckoutError = (error) => ({
  type: CHECKOUT_ACTION_TYPES.SET_CHECKOUT_ERROR,
  payload: error
});

export const clearCheckoutError = () => ({
  type: CHECKOUT_ACTION_TYPES.CLEAR_CHECKOUT_ERROR
});

// ============================================
// Helper: Validate Checkout Form
// ============================================

export const validateCheckoutForm = (formData, isAuthenticated) => {
  const errors = {};

  // Validate shipping address
  if (!formData.shippingAddress?.addressLine1) {
    errors.addressLine1 = 'Street address is required';
  }
  if (!formData.shippingAddress?.city) {
    errors.city = 'City is required';
  }
  if (!formData.shippingAddress?.state) {
    errors.state = 'State is required';
  }
  if (!formData.shippingAddress?.pincode) {
    errors.pincode = 'Pincode is required';
  }
  if (!formData.shippingAddress?.phone) {
    errors.phone = 'Phone number is required';
  } else if (!/^[6-9]\d{9}$/.test(formData.shippingAddress.phone)) {
    errors.phone = 'Please enter a valid 10-digit phone number';
  }

  // Validate guest info if not authenticated
  if (!isAuthenticated) {
    if (!formData.guestInfo?.name) {
      errors.guestName = 'Full name is required';
    }
    if (!formData.guestInfo?.email) {
      errors.guestEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.guestInfo.email)) {
      errors.guestEmail = 'Please enter a valid email address';
    }
    if (!formData.guestInfo?.phone) {
      errors.guestPhone = 'Phone number is required';
    }
  }

  // Validate payment method
  if (!formData.paymentMethod) {
    errors.paymentMethod = 'Please select a payment method';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
